import React, { useState } from 'react';
import { useReservation } from "./ReservationContext";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
   Alert,
   ButtonGroup,
   Button,
   FormControl,
   FormLabel,
   TextField,
   Typography,
   Switch,
   FormControlLabel,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import dayjs from "dayjs";
import "dayjs/locale/de"; // Deutsche Lokalisierung laden
import { updateSelectedDateTime } from './utils';
import { ThemeProvider } from '@mui/material/styles';
import { theme, themeClock } from './ColorTheme';
import DateTimePickerComponent from './DateTimePickerComponent';
import { useMatomo } from '@datapunt/matomo-tracker-react'

dayjs.locale("de"); // Lokalisierung auf Deutsch setzen

const ReservationStep1 = () => {
   const { reservationState, setReservationState } = useReservation();
   const { needsBabyChair, adults, children, restdays, selectedDate, selectedTime, isCritical, isSemiCritical } = reservationState;
   const { trackPageView } = useMatomo()
   const [open, setOpen] = useState(false);

   const handleOpen = () => {
      setOpen(true);
   };

   const handleClose = () => {
      setOpen(false);
   };

   // Track page view
   React.useEffect(() => {
      trackPageView({ documentTitle: 'Reservation Step 1' })
   }, [])

   const handleAdultsChange = (value) => {
      if (value + children <= 6) {
         setReservationState((prevState) => ({
            ...prevState,
            adults: value,
         }));
      }
   };

   const handleChildrenChange = (value) => {
      if (value + adults <= 6) {
         setReservationState((prevState) => ({
            ...prevState,
            children: value,
         }));
         if (value === 0) {
            setReservationState((prevState) => ({
               ...prevState,
               needsBabyChair: false,
            }));
         }
      }
   };

   return (
      <LocalizationProvider adapterLocale="de" dateAdapter={AdapterDayjs}>
         <Grid container spacing={2} className={"padded"}>
            <ThemeProvider theme={themeClock}>
               <Grid size={{ xs: 12 }}>
                  <Typography gutterBottom>
                     Bitte wähle Datum, Uhrzeit und die Anzahl der Personen aus. Beachte, dass Reservierungen für maximal 6 Personen möglich sind. Für Gruppenanfragen bitten wir Dich, uns telefonisch zu kontaktieren.
                  </Typography>
               </Grid>

               {/* Datum und Uhrzeit */}
               <Grid size={{ xs: 6 }}>
                  <DateTimePickerComponent restdays={restdays} setReservationState={setReservationState} selectedDate={selectedDate} selectedTime={selectedTime} />
               </Grid>

               {/* Uhrzeit */}
               <Grid size={{ xs: 6 }}>
                  <DesktopTimePicker
                     label="Uhrzeit"
                     color="baerendark"
                     skipDisabled
                     minTime={dayjs().hour(17).minute(40)}
                     maxTime={dayjs().hour(20).minute(5)}
                     open={open}
                     onOpen={handleOpen}
                     onClose={handleClose}
                     minutesStep={15}
                     value={selectedTime || null}
                     onChange={(newTime) => {
                        setReservationState((prevState) => ({
                           ...prevState,
                           selectedDateTime: updateSelectedDateTime(selectedDate, newTime),
                           selectedTime: newTime
                        }));
                     }}
                     slotProps={{
                        field: {
                           readOnly: true,
                           onClick: () => { handleOpen(); }
                        }
                     }}
                     renderInput={(params) =>
                        <TextField
                           color="baerendark"
                           fullWidth {...params}
                        />
                     }
                  />
               </Grid>
            </ThemeProvider>
            {(isSemiCritical || isCritical) && (
               <Grid size={{ xs: 12 }}>
                  <Alert severity={isCritical ? "error" : "warning"}>
                     {isCritical
                        ? "Es sind nur noch sehr wenige Tische verfügbar."
                        : "Nur noch wenige Tische verfügbar. Schnell reservieren!"}
                  </Alert>
               </Grid>
            )}
            <ThemeProvider theme={theme}>
               {/* Erwachsene */}
               <Grid size={{ xs: 12 }}>
                  <FormControl
                     component="fieldset"
                     sx={{
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        paddingTop: '0px'
                     }}
                  >
                     <FormLabel
                        component="legend"
                        sx={{ mb: 2, fontSize: "12px" }}
                     >
                        Anzahl Erwachsene
                     </FormLabel>
                     <ButtonGroup color="baerendark" variant="outlined">
                        {[...Array(6)].map((_, i) => (
                           <Button
                              key={i}
                              onClick={() => handleAdultsChange(i + 1)}
                              variant={adults === i + 1 ? "contained" : "outlined"}
                           >
                              {i + 1}
                           </Button>
                        ))}
                     </ButtonGroup>
                  </FormControl>
               </Grid>

               {/* Kinder */}
               <Grid size={{ xs: 12 }} mb={1}>
                  <FormControl
                     component="fieldset"
                     sx={{
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        paddingTop: '0px'
                     }}
                  >
                     <FormLabel
                        component="legend"
                        sx={{ mb: 2, fontSize: "12px" }}
                     >
                        Anzahl Kinder
                     </FormLabel>
                     <ButtonGroup color="baerendark" variant="outlined">
                        {[...Array(6)].map((_, i) => (
                           <>
                              <Button
                                 key={i}
                                 onClick={() => handleChildrenChange(i)}
                                 variant={children === i ? "contained" : "outlined"}
                              >
                                 {i}
                              </Button>
                           </>
                        ))}
                     </ButtonGroup>
                     {children > 0 && (
                        <FormControlLabel
                           sx={{ marginTop: "10px" }}
                           control={
                              <Switch
                                 checked={needsBabyChair}
                                 color="baerendark"
                                 onChange={(e) => {
                                    setReservationState((prevState) => ({
                                       ...prevState,
                                       needsBabyChair: e.target.checked,
                                    }));
                                 }}
                              />
                           }
                           label="Benötigst Du einen Babystuhl?"
                        />
                     )}
                  </FormControl>
               </Grid>
            </ThemeProvider>
         </Grid >
      </LocalizationProvider >
   );
};

export default ReservationStep1;
